import React, { useState } from 'react';
import SummaryCard from 'src/shared/components/card-templates/SummaryCard';
import { Badge } from 'src/shared/components/ui/badge';
import { SpeciesImage } from 'src/app/portfolio/components/species-image/SpeciesImage';
import IUCNStatusChip from 'src/shared/components/iucn-status-chip';
import { Loader } from 'src/shared/components/loader/Loader';
import { IUCNStatusCode } from 'src/shared/helpers/iucnStatusHelper';
import SiteThreatenedSpeciesModal from './SiteThreatenedSpeciesModal';
import SpeciesInfoModal from './SpeciesInfoModal';
import { ThreatenedSpecies } from '../../state/api/siteThreatenedSpeciesQuery';
import { useGetSiteThreatenedSpeciesQuery } from '../../state/api/siteNatureRiskEngineSlice';

type ThreatenedSpeciesWidgetProps = {
    organisationBusinessCentralId: string;
    siteId: string;
    onViewAll?: () => void;
    /** Optional overrides for the "View all" modal heading. */
    modalTitle?: string;
    modalSubtitle?: string;
};

// Preview count — the engine sorts most-threatened first and returns just these.
const PREVIEW_SIZE = 5;

const ThreatenedSpeciesWidget = ({
    organisationBusinessCentralId,
    siteId,
    onViewAll,
    modalTitle,
    modalSubtitle,
}: ThreatenedSpeciesWidgetProps) => {
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedSpecies, setSelectedSpecies] = useState<ThreatenedSpecies | null>(null);
    const { data, isLoading } = useGetSiteThreatenedSpeciesQuery(
        { organisationBusinessCentralId, siteId, page: 1, pageSize: PREVIEW_SIZE },
        { skip: !organisationBusinessCentralId || !siteId }
    );

    const handleViewAll = () => (onViewAll ? onViewAll() : setModalOpen(true));

    if (isLoading) {
        return (
            <SummaryCard
                cardIcon={null}
                containerClasses='gap-4 p-5'
                cardTitle='Threatened Species'
                cardBody={
                    <div className='flex min-h-[120px] items-center justify-center'>
                        <Loader />
                    </div>
                }
            />
        );
    }

    const widget = data?.siteThreatenedSpecies;
    const species = widget?.species.items ?? [];
    const total = widget?.species.pagination.totalItems ?? species.length;

    if (species.length === 0) {
        return (
            <p data-testid='threatened-species-empty' className='text-sm text-grey-80'>
                No threatened species recorded for this site.
            </p>
        );
    }

    return (
        <>
            <SummaryCard
                cardIcon={null}
                containerClasses='gap-4 p-5'
                cardTitle={
                    <span className='flex items-center gap-2'>
                        Threatened Species
                        <Badge variant='secondary' data-testid='threatened-species-count'>
                            {total}
                        </Badge>
                    </span>
                }
                action={
                    <button type='button' onClick={handleViewAll} className='text-sm font-medium text-primary'>
                        View all
                    </button>
                }
                cardBody={
                    <div data-testid='threatened-species-widget' className='flex flex-col gap-4'>
                        <div className='flex items-center justify-between text-sm font-semibold text-grey-80'>
                            <span>Species</span>
                            <span>IUCN Red List Status</span>
                        </div>

                        <ul className='flex flex-col'>
                            {species.map(item => (
                                <li key={item.scientificName} className='border-t border-grey-20'>
                                    <button
                                        type='button'
                                        onClick={() => setSelectedSpecies(item)}
                                        aria-label={`View details for ${item.commonName ?? item.scientificName}`}
                                        className='flex w-full cursor-pointer items-center gap-3 py-3 text-left hover:bg-grey-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary'
                                    >
                                        <SpeciesImage scientificName={item.scientificName} />
                                        <div className='flex min-w-0 flex-1 flex-col'>
                                            <span className='truncate font-semibold'>{item.commonName ?? item.scientificName}</span>
                                            {item.commonName && (
                                                <span className='truncate text-sm text-grey-80'>({item.scientificName})</span>
                                            )}
                                        </div>
                                        <IUCNStatusChip status={item.iucnCategory.toUpperCase() as IUCNStatusCode} />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                }
            />

            {modalOpen && (
                <SiteThreatenedSpeciesModal
                    onClose={() => setModalOpen(false)}
                    organisationBusinessCentralId={organisationBusinessCentralId}
                    siteId={siteId}
                    title={modalTitle}
                    subtitle={modalSubtitle}
                />
            )}

            {selectedSpecies && (
                <SpeciesInfoModal
                    onClose={() => setSelectedSpecies(null)}
                    scientificName={selectedSpecies.scientificName}
                    commonName={selectedSpecies.commonName}
                    iucnCategory={selectedSpecies.iucnCategory}
                    confidence={selectedSpecies.confidence}
                />
            )}
        </>
    );
};

export default ThreatenedSpeciesWidget;
